import {
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
  startTransition,
} from 'react'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { GrSearch } from 'react-icons/gr'
import { companyName } from '@app/configs'

// Re-usable classes
const PAGE_FIND_DRAWER_ID = '.pagefind-ui__drawer'
const PAGE_FIND_FORM = '.pagefind-ui__form'

export const PageFind = ({
  bundlePath,
  targetID,
}: {
  bundlePath?: string
  targetID?: string
}) => {
  const { t, lang } = useTranslation('common')
  const [loaded, setLoaded] = useState<boolean>(false)

  const pfFind = useRef<HTMLInputElement | null>(null)

  const textSearch = t('search')
  const searchText = targetID ? `${textSearch}...` : textSearch

  useEffect(() => {
    if (pfFind.current) {
      pfFind.current.placeholder = searchText
    }
  }, [lang, pfFind, searchText])

  useEffect(
    (_?: SyntheticEvent<HTMLInputElement>) => {
      if (!loaded) {
        let observer: MutationObserver

        const PagefindUI =
          // @ts-ignore
          typeof window.PagefindUI !== 'undefined' && window.PagefindUI

        if (PagefindUI) {
          try {
            const findID = targetID ? `#${targetID}` : '#search'

            new PagefindUI({
              element: findID,
              resetStyles: false,
              showImages: false,
              showEmptyFilters: false,
              bundlePath: bundlePath ?? '/_pagefind_blog/',
            })

            startTransition(() => {
              setLoaded(true)
            })

            const pagefindDrawer = document.querySelector(
              findID ? `${findID} ${PAGE_FIND_DRAWER_ID}` : PAGE_FIND_DRAWER_ID
            )

            // replace the .html from links with path
            if (pagefindDrawer) {
              pagefindDrawer.className = `${
                pagefindDrawer.className
              } absolute ${
                targetID
                  ? 'z-30 min-w-[90vw] -right-16 md:min-w-[40vw]'
                  : 'z-20'
              } bg-white dark:bg-black px-4 border dark:border-gray-600 shadow-xl mt-2 py-2 rounded md:right-0`

              const pagefindForm = document.querySelector(
                `${findID} .pagefind-ui > ${PAGE_FIND_FORM}`
              )

              if (pagefindForm) {
                pagefindForm.id = preId || 'search'
                pagefindForm.className = `${pagefindForm.className} min-w-[18.5rem]`
                const labelElement = document.createElement('label')
                labelElement.className = 'sr-only'
                labelElement.htmlFor = preId || 'search'
                labelElement.innerHTML = targetID
                  ? `${textSearch} ${companyName}`
                  : textSearch
                pagefindForm.insertAdjacentElement('afterend', labelElement)
              }

              pfFind.current = document.querySelector(
                `${findID} .pagefind-ui > ${PAGE_FIND_FORM} > .pagefind-ui__search-input`
              )

              let focused = 'no'

              // TODO: add supressed clear styles pagefind-ui__suppressed

              const onFocusInput = () => {
                // set event listener for background events
                if (
                  focused === 'backdrop' &&
                  pagefindDrawer.className.endsWith('pagefind-ui__hidden')
                ) {
                  pagefindDrawer.className = pagefindDrawer.className.replace(
                    'pagefind-ui__hidden',
                    ''
                  )
                }
                focused = 'active'
              }

              const onBlurFocusInput = () => {
                // set event listener for background events
                focused = 'blured'
              }

              const domCaptureClick = (e: MouseEvent) => {
                const targetElement = e && (e.target as HTMLElement | null)

                if (targetElement) {
                  if (
                    // check if inside the ui
                    typeof targetElement.className === 'string' &&
                    targetElement.className.startsWith('pagefind-ui__')
                  ) {
                    return
                  }
                  // ignore if conditions are met
                  if (
                    pagefindDrawer.className &&
                    !pagefindDrawer.className.endsWith('pagefind-ui__hidden')
                  ) {
                    pagefindDrawer.className = `${pagefindDrawer.className} pagefind-ui__hidden`
                    focused = 'backdrop'
                  }
                }
              }

              if (pfFind.current) {
                document.addEventListener('click', domCaptureClick)
                pfFind.current.addEventListener('focus', onFocusInput)
                pfFind.current.addEventListener('blur', onBlurFocusInput)
                pfFind.current.placeholder = searchText

                const preLabel = document.querySelector(
                  `label[for="${preId || 'search-focus'}"]`
                )
                if (!preLabel) {
                  const labelElement = document.createElement('label')
                  labelElement.className = 'sr-only'
                  labelElement.htmlFor =
                    pfFind.current.id || preId || 'search-focus'
                  labelElement.innerHTML = searchText
                  pfFind.current.insertAdjacentElement('afterend', labelElement)
                }
              }

              const guidesContainer =
                document.getElementById('articles-container')

              const callback = () => {
                const links: NodeListOf<HTMLAnchorElement> =
                  pagefindDrawer.querySelectorAll('.pagefind-ui__result-link')

                // determine container aria-hidden for keyboard searching
                if (guidesContainer) {
                  guidesContainer.ariaHidden = links.length ? 'true' : 'false'
                }

                for (const link of links) {
                  link.href = link.href.replace('.html', '')
                }
              }

              observer = new MutationObserver(callback)

              observer.observe(pagefindDrawer, {
                attributes: false,
                childList: true,
                subtree: true,
              })

              return () => {
                if (loaded) {
                  if (pfFind.current) {
                    document.removeEventListener('click', domCaptureClick)
                    pfFind.current.removeEventListener('focus', onFocusInput)
                    pfFind.current.removeEventListener('blur', onBlurFocusInput)
                  }
                  if (observer) {
                    observer.disconnect()
                  }
                }
              }
            }
          } catch (e) {
            console.error(e)
          }
        }
      }
    },
    [loaded, setLoaded, textSearch, searchText, bundlePath, targetID, pfFind]
  )

  const preId = targetID ? `${targetID}_pre` : 'target_pre'

  return (
    <>
      {!loaded ? (
        <>
          <label className='sr-only' htmlFor={preId}>
            {searchText}
          </label>
          <div className='flex relative text-xl font-bold place-items-center'>
            <GrSearch className='grIcon absolute left-5 text-[1.14rem] pointer-events-none text-gray-500 dark:text-gray-300' />
            <input
              className={`pagefind-ui__search-input ${
                targetID ? 'max-h-[45px] text-sm' : 'max-h-[64px]'
              } px-14 py-4 w-full appearance-none rounded-[var(--pagefind-ui-border-radius)] border-2 dark:border-gray-900 bg-[var(--pagefind-ui-background)] placeholder-gray-300 dark:placeholder-gray-400`}
              placeholder={searchText}
              type='search'
              id={preId}
              readOnly
            />
          </div>
        </>
      ) : null}
      <div
        id={targetID ?? 'search'}
        className={loaded ? undefined : 'sr-only'}
      ></div>
    </>
  )
}
